/**
 * CashRegisterRetryDialog (enhanced)
 *
 * Modal dialog shown when the Fiscat / cashier Bluetooth send times out
 * or fails. The dialog implements the following state machine:
 *
 *   timeout    – initial display, big visible 5→1 countdown then auto-retry
 *   retrying   – the receipt is currently being re-sent
 *   failed     – last retry failed; manual retry + (after 5 fails) BT-recovery
 *   btResetting / btWaiting / btReady – Bluetooth power-cycle in progress
 *
 * The dialog NEVER auto-dismisses on failure – the receipt payload must
 * always remain reachable so the operator can retry. Closing the dialog
 * defers the receipt: see `pendingCashRegisterReceipts.js` and
 * `OrderDetailsScreen.js` for the "Pénztárgép újraküldés" action that
 * re-opens this flow with the original payload.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    ActivityIndicator,
    Dimensions,
    Modal,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    Vibration,
    View,
} from 'react-native';
import LottieView from 'lottie-react-native';
import Toast from 'react-native-toast-message';
import {useTranslation} from "react-i18next";
import {registerCashRegisterRetryDialogHandler} from '../helpers/CashRegisterRetryDialogService';
import {haptics} from '../helpers/haptics';
import {
    isBluetoothToggleSupported,
    powerCycleBluetooth,
} from '../helpers/bluetoothRecovery';

const FIRST_RETRY_COOLDOWN_MS = 5000;
const FAILURE_RETRY_COOLDOWN_MS = 5000;
const BLUETOOTH_RECOVERY_THRESHOLD = 5;
const BLUETOOTH_OFF_DELAY_MS = 10000;

const {width} = Dimensions.get('window');

const STATUS = Object.freeze({
    TIMEOUT: 'timeout',
    RETRYING: 'retrying',
    FAILED: 'failed',
    BT_RESETTING: 'btResetting',
    BT_WAITING: 'btWaiting',
    BT_READY: 'btReady',
});

const CashRegisterRetryDialog = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState(STATUS.TIMEOUT);
    const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
    const [orderCode, setOrderCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [failureCount, setFailureCount] = useState(0);
    const [btCountdownSeconds, setBtCountdownSeconds] = useState(0);
    const [btPhase, setBtPhase] = useState(null);
    const [btRequiresManualToggle, setBtRequiresManualToggle] = useState(false);

    const attemptRetryRef = useRef(null);
    const resolveRef = useRef(null);
    const cooldownTimerRef = useRef(null);
    const autoRetryTriggeredRef = useRef(false);
    const performRetryRef = useRef(null);

    const clearCooldown = useCallback(() => {
        if (cooldownTimerRef.current) {
            globalThis.clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
        }
        setCooldownRemainingMs(0);
    }, []);

    const startCooldown = useCallback((durationMs, options = {}) => {
        clearCooldown();
        autoRetryTriggeredRef.current = false;
        const cooldownEndsAt = Date.now() + durationMs;
        const onComplete = options.onComplete;

        setCooldownRemainingMs(durationMs);
        cooldownTimerRef.current = globalThis.setInterval(() => {
            const remainingMs = Math.max(cooldownEndsAt - Date.now(), 0);
            setCooldownRemainingMs(remainingMs);

            if (Platform.OS === 'android') {
                const seconds = Math.ceil(remainingMs / 1000);
                if (seconds > 0 && seconds <= 5) {
                    haptics.select();
                }
            }

            if (remainingMs <= 0) {
                clearCooldown();
                if (typeof onComplete === 'function' && !autoRetryTriggeredRef.current) {
                    autoRetryTriggeredRef.current = true;
                    onComplete();
                }
            }
        }, 250);
    }, [clearCooldown]);

    const completeDialog = useCallback((result) => {
        clearCooldown();
        setVisible(false);
        setStatus(STATUS.TIMEOUT);
        setAttemptNumber(1);
        setFailureCount(0);
        setBtPhase(null);
        setBtCountdownSeconds(0);
        setBtRequiresManualToggle(false);
        attemptRetryRef.current = null;

        if (resolveRef.current) {
            resolveRef.current(result);
            resolveRef.current = null;
        }
    }, [clearCooldown]);

    const showRestartWarning = useCallback((retryError, nextFailureCount) => {
        const resolvedErrorMessage = retryError?.message || t('cash_register_not_responding');
        setErrorMessage(resolvedErrorMessage);
        setStatus(STATUS.FAILED);

        if (Platform.OS === 'android') {
            Vibration.vibrate([0, 450, 150, 450]);
        }

        Toast.show({
            type: 'error',
            text1: t('cash_register_not_responding'),
            text2: nextFailureCount >= BLUETOOTH_RECOVERY_THRESHOLD
                ? t('cash_register_start_bt_restart_hint')
                : t('cash_register_retry_resend_hint'),
            visibilityTime: 12000,
            position: 'top',
        });
    }, [t]);

    const performRetry = useCallback(async () => {
        const attemptRetry = attemptRetryRef.current;
        if (typeof attemptRetry !== 'function') {
            completeDialog(false);
            return;
        }

        const nextAttemptNumber = attemptNumber + 1;
        setAttemptNumber(nextAttemptNumber);
        setStatus(STATUS.RETRYING);
        clearCooldown();

        try {
            await attemptRetry();
            Toast.show({
                type: 'success',
                text1: t('cash_register_receipt_sent'),
                text2: orderCode ? t('cash_register_order_ref', { code: orderCode }) : undefined,
                visibilityTime: 5000,
            });
            completeDialog(true);
        } catch (retryError) {
            console.error('[CashRegisterRetryDialog] Retry failed:', retryError);
            const nextFailureCount = failureCount + 1;
            setFailureCount(nextFailureCount);
            showRestartWarning(retryError, nextFailureCount);
            if (nextFailureCount < BLUETOOTH_RECOVERY_THRESHOLD) {
                startCooldown(FAILURE_RETRY_COOLDOWN_MS, {
                    onComplete: () => {
                        if (performRetryRef.current) performRetryRef.current();
                    },
                });
            }
        }
    }, [
        attemptNumber,
        clearCooldown,
        completeDialog,
        failureCount,
        orderCode,
        showRestartWarning,
        startCooldown,
        t,
    ]);

    useEffect(() => {
        performRetryRef.current = performRetry;
    }, [performRetry]);

    const handleManualRetry = useCallback(() => {
        if (status === STATUS.RETRYING || status === STATUS.BT_RESETTING || status === STATUS.BT_WAITING) {
            return;
        }
        performRetry();
    }, [performRetry, status]);

    const handleBluetoothRecovery = useCallback(async () => {
        setStatus(STATUS.BT_RESETTING);
        setBtPhase('disabling');
        setBtCountdownSeconds(Math.ceil(BLUETOOTH_OFF_DELAY_MS / 1000));
        setBtRequiresManualToggle(!isBluetoothToggleSupported());

        try {
            const result = await powerCycleBluetooth({
                offDelayMs: BLUETOOTH_OFF_DELAY_MS,
                onProgress: ({phase, remainingMs}) => {
                    setBtPhase(phase);
                    if (phase === 'waiting') {
                        setStatus(STATUS.BT_WAITING);
                        setBtCountdownSeconds(Math.ceil(remainingMs / 1000));
                    } else if (phase === 'enabling') {
                        setBtCountdownSeconds(0);
                    }
                },
            });

            if (result?.requiresManualToggle) {
                setBtRequiresManualToggle(true);
            }

            setStatus(STATUS.BT_READY);
            setBtPhase('done');
            setFailureCount(0);
            await performRetry();
        } catch (error) {
            console.error('[CashRegisterRetryDialog] BT recovery failed:', error);
            setStatus(STATUS.FAILED);
            setBtPhase(null);
            setBtCountdownSeconds(0);
            setErrorMessage(error?.message || t('cash_register_bt_restart_failed'));
        }
    }, [performRetry, t]);

    const handleCloseAfterFailure = useCallback(() => {
        if (status === STATUS.RETRYING || status === STATUS.BT_RESETTING || status === STATUS.BT_WAITING) {
            return;
        }
        completeDialog(false);
    }, [completeDialog, status]);

    const openDialog = useCallback((options = {}) => {
        return new Promise((resolve) => {
            const initialError = options.initialError || options.error;

            attemptRetryRef.current = options.attemptRetry;
            resolveRef.current = resolve;
            setOrderCode(options.orderCode || '');
            setErrorMessage(initialError?.message || t('cash_register_communication_timeout'));
            setAttemptNumber(1);
            setFailureCount(0);
            setBtPhase(null);
            setBtCountdownSeconds(0);
            setBtRequiresManualToggle(false);
            setStatus(STATUS.TIMEOUT);
            setVisible(true);
            startCooldown(FIRST_RETRY_COOLDOWN_MS, {
                onComplete: () => {
                    if (performRetryRef.current) performRetryRef.current();
                },
            });
        });
    }, [startCooldown, t]);

    useEffect(() => {
        const unregister = registerCashRegisterRetryDialogHandler(openDialog);

        return () => {
            unregister();
            clearCooldown();
        };
    }, [clearCooldown, openDialog]);

    const cooldownSeconds = Math.ceil(cooldownRemainingMs / 1000);
    const isFailure = status === STATUS.FAILED;
    const isBusy = status === STATUS.RETRYING
        || status === STATUS.BT_RESETTING
        || status === STATUS.BT_WAITING;
    const showBluetoothRecovery = (isFailure && failureCount >= BLUETOOTH_RECOVERY_THRESHOLD);
    const retryDisabled = isBusy;

    const copy = useMemo(() => {
        if (status === STATUS.RETRYING) {
            return {
                title: t('cash_register_resending_title'),
                message: t('cash_register_resending_message'),
            };
        }

        if (status === STATUS.BT_RESETTING || status === STATUS.BT_WAITING) {
            return {
                title: btRequiresManualToggle
                    ? t('cash_register_bt_manual_toggle_title')
                    : t('cash_register_bt_restarting_title'),
                message: btRequiresManualToggle
                    ? t('cash_register_bt_manual_toggle_message')
                    : t('cash_register_bt_restarting_message'),
            };
        }

        if (status === STATUS.BT_READY) {
            return {
                title: t('cash_register_bt_restarted_title'),
                message: t('cash_register_bt_restarted_message'),
            };
        }

        if (showBluetoothRecovery) {
            return {
                title: t('cash_register_not_responding'),
                message: t('cash_register_recovery_message', { count: BLUETOOTH_RECOVERY_THRESHOLD }),
            };
        }

        if (isFailure) {
            return {
                title: t('cash_register_not_responding'),
                message: t('cash_register_failure_message'),
            };
        }

        return {
            title: t('cash_register_timeout_title'),
            message: t('cash_register_retry_timeout_message'),
        };
    }, [btRequiresManualToggle, isFailure, showBluetoothRecovery, status, t]);

    const showCountdown = (status === STATUS.TIMEOUT) && cooldownRemainingMs > 0;
    const showBtCountdown = (status === STATUS.BT_WAITING) && btCountdownSeconds > 0;
    const isRetryingState = status === STATUS.RETRYING || status === STATUS.BT_RESETTING || status === STATUS.BT_WAITING;

    if (!visible) {
        return null;
    }

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={isFailure ? handleCloseAfterFailure : undefined}
        >
            <View style={styles.backdrop}>
                <View style={[styles.card, isFailure && styles.failureCard]}>
                    <View style={styles.animationWrapper}>
                        {isRetryingState ? (
                            <ActivityIndicator size="large" color="#F76E24" />
                        ) : (
                            <LottieView
                                source={isFailure
                                    ? require('../../assets/animations/failed_center.json')
                                    : require('../../assets/animations/printing-ticket.json')}
                                autoPlay
                                loop={!isFailure}
                                style={styles.animation}
                            />
                        )}
                    </View>

                    <Text style={[styles.title, isFailure && styles.failureTitle]}>{copy.title}</Text>
                    {orderCode ? <Text style={styles.orderCode}>{t('cash_register_order_label', { code: orderCode })}</Text> : null}
                    <Text style={styles.message}>{copy.message}</Text>

                    {showCountdown ? (
                        <View style={styles.countdownContainer}>
                            <Text style={styles.countdownLabel}>{t('cash_register_auto_resend_label')}</Text>
                            <Text style={styles.countdownDigit}>{cooldownSeconds}</Text>
                        </View>
                    ) : null}

                    {showBtCountdown ? (
                        <View style={styles.countdownContainer}>
                            <Text style={styles.countdownLabel}>
                                {btRequiresManualToggle ? t('cash_register_switch_back_label') : t('cash_register_bt_reconnect_label')}
                            </Text>
                            <Text style={[styles.countdownDigit, styles.countdownDigitBlue]}>{btCountdownSeconds}</Text>
                        </View>
                    ) : null}

                    {errorMessage && status !== STATUS.RETRYING ? (
                        <View style={styles.errorBox}>
                            <Text style={styles.errorLabel}>{t('cash_register_technical_error_label', { count: failureCount })}</Text>
                            <Text style={styles.errorText}>{errorMessage}</Text>
                        </View>
                    ) : null}

                    {showBluetoothRecovery ? (
                        <TouchableOpacity
                            style={[styles.retryButton, styles.btResetButton, retryDisabled && styles.retryButtonDisabled]}
                            onPress={handleBluetoothRecovery}
                            disabled={retryDisabled}
                        >
                            <Text style={styles.retryButtonText}>
                                {t('cash_register_bt_restart_retry_button')}
                            </Text>
                        </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                        style={[styles.retryButton, retryDisabled && styles.retryButtonDisabled]}
                        onPress={handleManualRetry}
                        disabled={retryDisabled}
                    >
                        <Text style={styles.retryButtonText}>
                            {status === STATUS.RETRYING
                                ? t('cash_register_sending_in_progress')
                                : status === STATUS.BT_RESETTING || status === STATUS.BT_WAITING
                                    ? t('cash_register_bt_restart_in_progress')
                                    : showCountdown
                                        ? t('cash_register_resend_now_countdown', { seconds: cooldownSeconds })
                                        : t('cash_register_resend_receipt_button')}
                        </Text>
                    </TouchableOpacity>

                    {(isFailure || showBluetoothRecovery) ? (
                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={handleCloseAfterFailure}
                            disabled={isBusy}
                        >
                            <Text style={styles.closeButtonText}>{t('cash_register_close_retry_later')}</Text>
                        </TouchableOpacity>
                    ) : null}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        padding: 24,
    },
    card: {
        width: Math.min(width * 0.9, 560),
        borderRadius: 22,
        padding: 28,
        alignItems: 'center',
        backgroundColor: '#fff',
        borderWidth: 4,
        borderColor: '#F76E24',
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.35,
        shadowRadius: 16,
        elevation: 16,
    },
    failureCard: {
        borderColor: '#D90429',
        backgroundColor: '#FFF5F5',
    },
    animationWrapper: {
        width: 148,
        height: 148,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
    },
    animation: {
        width: 148,
        height: 148,
    },
    title: {
        fontSize: 28,
        fontWeight: '900',
        color: '#2B2B2B',
        textAlign: 'center',
        marginBottom: 8,
    },
    failureTitle: {
        color: '#D90429',
    },
    orderCode: {
        fontSize: 20,
        fontWeight: '800',
        color: '#F76E24',
        marginBottom: 12,
    },
    message: {
        fontSize: 18,
        lineHeight: 26,
        color: '#333',
        textAlign: 'center',
        marginBottom: 18,
    },
    countdownContainer: {
        alignItems: 'center',
        marginBottom: 18,
    },
    countdownLabel: {
        fontSize: 16,
        color: '#555',
        marginBottom: 4,
        fontWeight: '700',
    },
    countdownDigit: {
        fontSize: 72,
        fontWeight: '900',
        color: '#F76E24',
        lineHeight: 80,
    },
    countdownDigitBlue: {
        color: '#1E88E5',
    },
    errorBox: {
        width: '100%',
        borderRadius: 12,
        padding: 12,
        backgroundColor: '#2B2B2B',
        marginBottom: 18,
    },
    errorLabel: {
        color: '#FFCF99',
        fontWeight: '800',
        marginBottom: 4,
    },
    errorText: {
        color: '#fff',
        fontSize: 14,
        textAlign: 'center',
    },
    retryButton: {
        width: '100%',
        borderRadius: 14,
        paddingVertical: 18,
        paddingHorizontal: 16,
        backgroundColor: '#D90429',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 4,
    },
    btResetButton: {
        backgroundColor: '#1E88E5',
        marginBottom: 8,
    },
    retryButtonDisabled: {
        backgroundColor: '#9D9D9D',
    },
    retryButtonText: {
        color: '#fff',
        fontSize: 20,
        fontWeight: '900',
        textAlign: 'center',
    },
    closeButton: {
        marginTop: 14,
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    closeButtonText: {
        color: '#555',
        fontSize: 16,
        fontWeight: '700',
        textAlign: 'center',
    },
});

export default CashRegisterRetryDialog;
